/**
 * © 2026 Grupo Salamander SAPI de CV
 *
 * Material Symbols product name → Framework7 glyph mapping (plan task 1.7).
 * Ported from the retired `Theme/next/iconSet.ts`'s `ICON_MAP` — ownership
 * of the icon-set decision (Framework7 over Material Symbols) is recorded
 * there in git history and in `docs/design/plans/notes-ui-baseline.md`.
 *
 * Keys are the Material Symbols product names already used across the
 * codebase and stored in the database (`./manifest.json`,
 * `./names.generated.ts`). Keeping the key stable means the database rows
 * and every call site keep working through an icon-rendering-font change —
 * only this mapping and `./index.tsx`'s rendering swap.
 *
 * `fill` is the filled twin where Framework7 has one (verified against the
 * real `framework7-icons@5.0.5` package, 35 of 70). Where it is `null` the
 * outline glyph is used for both states, and selection must be carried by
 * colour/weight instead — honest about the set's limits rather than
 * silently rendering the unselected glyph as if it were selected.
 */
import type { TUIIconName } from './names.generated'

type TIconMapEntry = {
  glyph: string
  fill: string | null
}

export const ICON_MAP: Record<TUIIconName, TIconMapEntry> = {
  account_balance_wallet: {
    glyph: 'money_dollar_circle',
    fill: 'money_dollar_circle_fill',
  },
  add: { glyph: 'plus', fill: null },
  add_card: { glyph: 'creditcard', fill: 'creditcard_fill' },
  // The next two (and group/handshake/link/shield/smart_toy/support_agent/
  // view_list/work below) were added 2026-09-23 while implementing task
  // 1.8 — packages/portal/src/navigation/index.tsx's real sidebar tree
  // uses them through a local helper the original manifest audit's grep
  // missed (see manifest.json's codeUsage.description).
  admin_panel_settings: { glyph: 'lock_shield', fill: 'lock_shield_fill' },
  architecture: { glyph: 'building_columns', fill: 'building_columns_fill' },
  arrow_back: { glyph: 'arrow_left', fill: null },
  arrow_downward: { glyph: 'arrow_down', fill: null },
  arrow_upward: { glyph: 'arrow_up', fill: null },
  attach_file: { glyph: 'paperclip', fill: null },
  // No real filled glyph exists for this one in framework7-icons@5.0.5 — the
  // only calendar-family fill, `calendar_circle_fill`, is a circle-badge
  // glyph (a different silhouette), not a plain filled calendar. Outline
  // only until a better glyph exists (notes-ui-baseline.md, 2026-09-23).
  calendar_month: { glyph: 'calendar', fill: null },
  cancel: { glyph: 'xmark_circle', fill: 'xmark_circle_fill' },
  category: { glyph: 'square_grid_2x2', fill: 'square_grid_2x2_fill' },
  chat: { glyph: 'chat_bubble_2', fill: 'chat_bubble_2_fill' },
  chat_bubble: { glyph: 'chat_bubble', fill: 'chat_bubble_fill' },
  check: { glyph: 'checkmark', fill: null },
  check_circle: { glyph: 'checkmark_circle', fill: 'checkmark_circle_fill' },
  chevron_left: { glyph: 'chevron_left', fill: null },
  chevron_right: { glyph: 'chevron_right', fill: null },
  close: { glyph: 'xmark', fill: null },
  content_copy: { glyph: 'doc_on_doc', fill: 'doc_on_doc_fill' },
  credit_card: { glyph: 'creditcard', fill: 'creditcard_fill' },
  currency_exchange: { glyph: 'arrow_2_circlepath', fill: null },
  dashboard: { glyph: 'rectangle_grid_2x2', fill: 'rectangle_grid_2x2_fill' },
  delete: { glyph: 'trash', fill: 'trash_fill' },
  description: { glyph: 'doc_text', fill: 'doc_text_fill' },
  dock_to_left: { glyph: 'sidebar_left', fill: null },
  dock_to_right: { glyph: 'sidebar_right', fill: null },
  download: { glyph: 'arrow_down_to_line', fill: null },
  dynamic_form: { glyph: 'list_bullet_indent', fill: null },
  edit: { glyph: 'pencil', fill: null },
  error: {
    glyph: 'exclamationmark_circle',
    fill: 'exclamationmark_circle_fill',
  },
  expand_less: { glyph: 'chevron_up', fill: null },
  expand_more: { glyph: 'chevron_down', fill: null },
  file_download: { glyph: 'arrow_down_doc', fill: 'arrow_down_doc_fill' },
  filter_list: { glyph: 'line_horizontal_3_decrease', fill: null },
  flight: { glyph: 'airplane', fill: null },
  format_align_center: { glyph: 'text_aligncenter', fill: null },
  format_align_left: { glyph: 'text_alignleft', fill: null },
  format_align_right: { glyph: 'text_alignright', fill: null },
  format_bold: { glyph: 'bold', fill: null },
  format_italic: { glyph: 'italic', fill: null },
  format_underlined: { glyph: 'underline', fill: null },
  group: { glyph: 'person_2', fill: 'person_2_fill' },
  handshake: { glyph: 'hand_raised', fill: 'hand_raised_fill' },
  help: { glyph: 'question_circle', fill: 'question_circle_fill' },
  home: { glyph: 'house', fill: 'house_fill' },
  image: { glyph: 'photo', fill: 'photo_fill' },
  info: { glyph: 'info_circle', fill: 'info_circle_fill' },
  input: { glyph: 'square_arrow_right', fill: 'square_arrow_right_fill' },
  inventory_2: { glyph: 'archivebox', fill: 'archivebox_fill' },
  label: { glyph: 'tag', fill: 'tag_fill' },
  library_books: { glyph: 'book', fill: 'book_fill' },
  link: { glyph: 'link', fill: null },
  link_off: { glyph: 'link', fill: null },
  list_alt: { glyph: 'list_bullet_below_rectangle', fill: null },
  mail: { glyph: 'envelope', fill: 'envelope_fill' },
  menu: { glyph: 'line_horizontal_3', fill: null },
  more_horiz: { glyph: 'ellipsis', fill: null },
  more_vert: { glyph: 'ellipsis_vertical', fill: null },
  notifications: { glyph: 'bell', fill: 'bell_fill' },
  open_in_new: {
    glyph: 'arrow_up_right_square',
    fill: 'arrow_up_right_square_fill',
  },
  payments: { glyph: 'money_dollar', fill: null },
  person: { glyph: 'person', fill: 'person_fill' },
  receipt: { glyph: 'doc_text', fill: 'doc_text_fill' },
  receipt_long: { glyph: 'doc_plaintext', fill: null },
  redo: { glyph: 'arrow_uturn_right', fill: null },
  save: { glyph: 'tray_arrow_down', fill: 'tray_arrow_down_fill' },
  search: { glyph: 'search', fill: null },
  send: { glyph: 'paperplane', fill: 'paperplane_fill' },
  settings: { glyph: 'gear_alt', fill: 'gear_alt_fill' },
  share: { glyph: 'square_arrow_up', fill: 'square_arrow_up_fill' },
  shield: { glyph: 'shield', fill: 'shield_fill' },
  smart_toy: { glyph: 'sparkles', fill: null },
  star: { glyph: 'star', fill: 'star_fill' },
  support_agent: { glyph: 'headphones', fill: null },
  tune: { glyph: 'slider_horizontal_3', fill: null },
  undo: { glyph: 'arrow_uturn_left', fill: null },
  view_list: { glyph: 'list_bullet', fill: null },
  visibility: { glyph: 'eye', fill: 'eye_fill' },
  visibility_off: { glyph: 'eye_slash', fill: 'eye_slash_fill' },
  warning: {
    glyph: 'exclamationmark_triangle',
    fill: 'exclamationmark_triangle_fill',
  },
  work: { glyph: 'briefcase', fill: 'briefcase_fill' },
  workspaces: { glyph: 'circle_grid_3x3', fill: 'circle_grid_3x3_fill' },

  // The 17 names below were added 2026-09-23 during PR #186 review: the
  // pipeline-builder admin editors (packages/portal/src/components/
  // LineOfBusiness/pipeline-builder/panels/*, .../Forms/editor/*) rendered
  // Material Symbols product names directly through raw MUI `Icon`
  // (ligature text) instead of UIIcon, so the original manifest audit
  // (scoped to UIIcon call sites) never found them.
  arrow_drop_down_circle: {
    glyph: 'chevron_down_circle',
    fill: 'chevron_down_circle_fill',
  },
  attach_money: { glyph: 'money_dollar', fill: null },
  cake: { glyph: 'gift', fill: 'gift_fill' },
  calendar_today: { glyph: 'calendar_today', fill: null },
  checklist: {
    glyph: 'checkmark_rectangle',
    fill: 'checkmark_rectangle_fill',
  },
  cloud_download: { glyph: 'cloud_download', fill: 'cloud_download_fill' },
  email: { glyph: 'envelope', fill: 'envelope_fill' },
  // Marketing (epic 121): glyphs picked from the same framework7-icons set.
  campaign: { glyph: 'antenna_radiowaves_left_right', fill: null },
  contacts: {
    glyph: 'person_crop_rectangle',
    fill: 'person_crop_rectangle_fill',
  },
  mark_email_read: { glyph: 'envelope_open', fill: 'envelope_open_fill' },
  unsubscribe: { glyph: 'person_badge_minus', fill: 'person_badge_minus_fill' },
  format_indent_decrease: { glyph: 'list_bullet', fill: null },
  format_indent_increase: { glyph: 'list_bullet_indent', fill: null },
  password: { glyph: 'lock', fill: 'lock_fill' },
  percent: { glyph: 'percent', fill: null },
  phone: { glyph: 'phone', fill: 'phone_fill' },
  // Material's own 'pin' glyph is a map-pin/thumbtack shape (used here for
  // a numeric field's icon, per FieldRow.tsx's FIELD_TYPE_ICONS — an odd
  // original choice, not this task's to correct; only the rendering font
  // changes). `placemark` is Framework7's real map-pin equivalent.
  pin: { glyph: 'placemark', fill: 'placemark_fill' },
  radio_button_checked: { glyph: 'largecircle_fill_circle', fill: null },
  rule: { glyph: 'line_horizontal_3', fill: null },
  text_fields: { glyph: 'textformat', fill: null },
  toggle_on: { glyph: 'checkmark_square', fill: 'checkmark_square_fill' },
}

/**
 * Resolve a product name to the Framework7 glyph text for the given state.
 *
 * `name` is `TUIIconName` at the type level, so a well-typed caller can
 * never reach the fallback below. It exists for a value that reached this
 * component unsafely typed (an untyped call site, a cast) rather than
 * through `toValidIconName` (`./validate.ts`) at its actual runtime
 * boundary — the same "fall back to a visible glyph and warn instead of
 * crashing" contract task 1.8 asks for at the database boundary, applied
 * here as a last line of defence rather than a substitute for it.
 */
export function resolveGlyph(
  name: TUIIconName,
  fill: boolean
): { glyph: string; canFill: boolean } {
  const entry = ICON_MAP[name] ?? ICON_MAP[FALLBACK_ENTRY_NAME]
  if (!ICON_MAP[name]) {
    console.warn(
      `UIIcon: "${String(name)}" is not one of the closed set of Material ` +
        'Symbols names (packages/renaissance/src/components/UIIcon/manifest.json). ' +
        `Falling back to "${FALLBACK_ENTRY_NAME}".`
    )
  }
  return {
    glyph: fill && entry.fill ? entry.fill : entry.glyph,
    canFill: entry.fill !== null,
  }
}

const FALLBACK_ENTRY_NAME: TUIIconName = 'help'
